/**
 * traffic.ts — street-network TRAFFIC ranking (W2d-4 Task 1).
 *
 * The desire-line / promotion pass (spec §"Desire-line / promotion pass") needs
 * two deterministic measures over the pre-parcel street graph:
 *
 *   - {@link edgeBetweenness} — Brandes' algorithm, LENGTH-WEIGHTED (a road's
 *     traffic load is about travel distance, not hop count), accumulated on
 *     EDGES. The top-ranked runs become MAIN STREETS; the settlement's
 *     top-ranked junction hosts the PLAZA.
 *   - {@link pathCrossings} — how many of the sampled high-traffic shortest
 *     paths traverse each edge. ALLEY/TERTIARY edges crossed at least the
 *     tier's threshold PROMOTE a class ("footpath promotion").
 *
 * ENDPOINTS ({@link trafficEndpoints}): the settlement seed(s), the border
 * ports (through-traffic), and the highest-degree junctions (local
 * destinations). STAGING NOTE (documented deviation): the promotion pass runs
 * BEFORE parcels exist (so widened main streets shape the lot setbacks), so
 * required-building parcels cannot anchor endpoints — the top-degree junctions
 * stand in for them.
 *
 * DETERMINISM. Edge lengths via pmath.hypot (bit-exact); Dijkstra extract-min
 * breaks distance ties by ascending node index; neighbour relaxation walks
 * incident edges in ascending edge order; shortest-path predecessor choice is
 * the first-found under that order. Pair sampling is a registered hashInts
 * stream. No Set/Map iteration-order dependence. The pmath-ban gate covers
 * this file.
 */
import * as pmath from "../pmath.js";
import { hashInts, h01 } from "./hash.js";

/** Minimal graph view (structurally satisfied by CityGraph and builder snapshots). */
export interface TrafficGraphView {
  nodeU: ArrayLike<number>;
  nodeV: ArrayLike<number>;
  edgeA: ArrayLike<number>;
  edgeB: ArrayLike<number>;
}

/** Pair-sampling salt (hash.ts registry). */
export const SALT_TRAFFIC = 0x7fa1;

/** Incident-edge lists in ascending edge order (only `eligible` edges). */
function incidence(g: TrafficGraphView, eligible?: (e: number) => boolean): number[][] {
  const inc: number[][] = Array.from({ length: g.nodeU.length }, () => []);
  for (let e = 0; e < g.edgeA.length; e++) {
    if (eligible !== undefined && !eligible(e)) continue;
    const a = g.edgeA[e], b = g.edgeB[e];
    if (a === b) continue;
    const len = pmath.hypot(g.nodeU[b] - g.nodeU[a], g.nodeV[b] - g.nodeV[a]);
    if (len < 1e-9) continue;
    inc[a].push(e);
    inc[b].push(e);
  }
  return inc;
}

/** Deterministic Dijkstra from `src`: fills dist + first-found predecessor edge. */
function dijkstra(
  g: TrafficGraphView, inc: number[][], src: number,
  dist: Float64Array, predEdge: Int32Array, order: number[],
): void {
  const n = g.nodeU.length;
  dist.fill(Infinity);
  predEdge.fill(-1);
  order.length = 0;
  const done = new Uint8Array(n);
  dist[src] = 0;
  for (;;) {
    // Extract-min with ascending-index tie-break (deterministic).
    let u = -1, best = Infinity;
    for (let i = 0; i < n; i++) {
      if (!done[i] && dist[i] < best) { best = dist[i]; u = i; }
    }
    if (u < 0) break;
    done[u] = 1;
    order.push(u);
    for (const e of inc[u]) {
      const v = g.edgeA[e] === u ? g.edgeB[e] : g.edgeA[e];
      if (done[v]) continue;
      const w = pmath.hypot(g.nodeU[v] - g.nodeU[u], g.nodeV[v] - g.nodeV[u]);
      const nd = dist[u] + w;
      if (nd < dist[v] - 1e-12) { dist[v] = nd; predEdge[v] = e; }
    }
  }
}

/**
 * Length-weighted EDGE betweenness (Brandes 2001, edge variant): for every
 * source node, shortest-path DAG sigma counts + dependency accumulation onto
 * edges. Undirected double-count halved. `eligible` restricts the network
 * (the promotion pass excludes bridges/stairs from ranking but they still
 * carry paths — pass the same predicate to both measures for consistency).
 * O(V·(V+E) log-less) with the simple extract-min — street graphs are ≤ ~1k
 * nodes, well inside the region budget.
 */
export function edgeBetweenness(g: TrafficGraphView, eligible?: (e: number) => boolean): Float64Array {
  const n = g.nodeU.length;
  const inc = incidence(g, eligible);
  const eb = new Float64Array(g.edgeA.length);
  const dist = new Float64Array(n);
  const sigma = new Float64Array(n);
  const delta = new Float64Array(n);
  const done = new Uint8Array(n);
  const order: number[] = [];
  for (let s = 0; s < n; s++) {
    if (inc[s].length === 0) continue;
    // Dijkstra with FULL predecessor sets (sigma needs every shortest tie).
    dist.fill(Infinity);
    sigma.fill(0);
    delta.fill(0);
    done.fill(0);
    order.length = 0;
    const preds: number[][] = Array.from({ length: n }, () => []); // predecessor EDGES
    dist[s] = 0;
    sigma[s] = 1;
    for (;;) {
      let u = -1, best = Infinity;
      for (let i = 0; i < n; i++) {
        if (!done[i] && dist[i] < best) { best = dist[i]; u = i; }
      }
      if (u < 0) break;
      done[u] = 1;
      order.push(u);
      for (const e of inc[u]) {
        const v = g.edgeA[e] === u ? g.edgeB[e] : g.edgeA[e];
        if (done[v]) continue;
        const w = pmath.hypot(g.nodeU[v] - g.nodeU[u], g.nodeV[v] - g.nodeV[u]);
        const nd = dist[u] + w;
        if (nd < dist[v] - 1e-9) {
          dist[v] = nd;
          sigma[v] = sigma[u];
          preds[v].length = 0;
          preds[v].push(e);
        } else if (Math.abs(nd - dist[v]) <= 1e-9) {
          sigma[v] += sigma[u];
          preds[v].push(e);
        }
      }
    }
    // Dependency accumulation in reverse settle order.
    for (let i = order.length - 1; i >= 0; i--) {
      const w = order[i];
      for (const e of preds[w]) {
        const v = g.edgeA[e] === w ? g.edgeB[e] : g.edgeA[e];
        const c = (sigma[v] / sigma[w]) * (1 + delta[w]);
        eb[e] += c;
        delta[v] += c;
      }
    }
  }
  for (let e = 0; e < eb.length; e++) eb[e] *= 0.5; // undirected double count
  return eb;
}

/**
 * The traffic endpoint set: seed nodes + port nodes + the top-degree junctions
 * (ascending node index within equal degree — deterministic), deduped, capped
 * at `maxJunctions` junction entries. `nodeKind` uses citygraph's NODE_* codes
 * (passed as plain numbers to keep this module import-light).
 */
export function trafficEndpoints(
  g: TrafficGraphView, nodeKind: ArrayLike<number>,
  seedKind: number, portKind: number, maxJunctions = 8,
  eligible?: (e: number) => boolean,
): number[] {
  const inc = incidence(g, eligible);
  const out: number[] = [];
  for (let i = 0; i < nodeKind.length; i++) {
    if ((nodeKind[i] === seedKind || nodeKind[i] === portKind) && inc[i].length > 0) out.push(i);
  }
  // Top-degree junctions (degree desc, index asc), excluding already-picked.
  const junctions: number[] = [];
  for (let i = 0; i < nodeKind.length; i++) {
    if (nodeKind[i] !== seedKind && nodeKind[i] !== portKind && inc[i].length >= 3) junctions.push(i);
  }
  junctions.sort((a, b) => (inc[b].length - inc[a].length) || (a - b));
  for (let j = 0; j < junctions.length && j < maxJunctions; j++) out.push(junctions[j]);
  return out;
}

/**
 * Hash-sample `count` DISTINCT endpoint pairs (SALT_TRAFFIC stream — one draw
 * pair per sample index). Fewer than 2 endpoints → no pairs. A drawn duplicate
 * pair is kept (it just re-weights that desire line — deterministic and cheap).
 */
export function samplePairs(
  planetHash: number, cellKey: number, k: number, endpoints: number[], count: number,
): [number, number][] {
  const out: [number, number][] = [];
  const n = endpoints.length;
  if (n < 2) return out;
  for (let i = 0; i < count; i++) {
    const a = Math.floor(h01(hashInts(planetHash, SALT_TRAFFIC, cellKey, k, i * 2)) * n);
    let b = Math.floor(h01(hashInts(planetHash, SALT_TRAFFIC, cellKey, k, i * 2 + 1)) * (n - 1));
    if (b >= a) b++;
    out.push([endpoints[a], endpoints[b]]);
  }
  return out;
}

/**
 * Per-edge count of sampled shortest paths traversing it (the desire lines).
 * One deterministic Dijkstra per unique source; the path walks first-found
 * predecessors from target back to source.
 */
export function pathCrossings(
  g: TrafficGraphView, pairs: [number, number][], eligible?: (e: number) => boolean,
): Uint32Array {
  const n = g.nodeU.length;
  const inc = incidence(g, eligible);
  const counts = new Uint32Array(g.edgeA.length);
  const dist = new Float64Array(n);
  const predEdge = new Int32Array(n);
  const order: number[] = [];
  // Group pairs by source so each source runs Dijkstra once (ascending source).
  const bySrc = new Map<number, number[]>();
  for (const [a, b] of pairs) {
    let arr = bySrc.get(a);
    if (arr === undefined) { arr = []; bySrc.set(a, arr); }
    arr.push(b);
  }
  const sources = [...bySrc.keys()].sort((x, y) => x - y);
  for (const s of sources) {
    dijkstra(g, inc, s, dist, predEdge, order);
    for (const t of bySrc.get(s)!) {
      if (!(dist[t] < Infinity)) continue; // disconnected (should not happen post-R1)
      let v = t;
      let guard = 0;
      while (v !== s && guard++ <= g.edgeA.length) {
        const e = predEdge[v];
        if (e < 0) break;
        counts[e]++;
        v = g.edgeA[e] === v ? g.edgeB[e] : g.edgeA[e];
      }
    }
  }
  return counts;
}
